import React, {useContext, useRef} from "react";
import {
    Box, Button,
    Checkbox,
    Divider,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Slider,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {alpha} from '@mui/material/styles';
import {toast} from "react-toastify";
import {IconLock} from "@tabler/icons-react";
import {grey} from "@mui/material/colors";
import {WebSettingsContext} from "../../../../../context/WebSettingsContext";

const PHASES = [
    {
        value: "TEACHER_PRIMARY",
        label: "Phase 1 — Teacher primary",
        description: "The teacher serves users. Optional student shadow decisions are trace-only."
    },
    {
        value: "STUDENT_GATED",
        label: "Phase 2 — Student fully gated",
        description: "Every student response and action is reviewed before delivery or execution."
    },
    {
        value: "STUDENT_SUPERVISED",
        label: "Phase 3 — Student supervised",
        description: "The teacher reviews protected, failed, unusual, and sampled decisions."
    },
    {
        value: "STUDENT_AUTONOMOUS",
        label: "Phase 4 — Student autonomous",
        description: "The student serves users while deterministic checks and sampled audits remain active."
    },
];
const OPENAI_DEFAULTS = {
    provider: "openai",
    api_mode: "responses",
    base_url: "https://api.openai.com/v1",
    credential_ref: "secret://openai/api-key",
};

const DEFAULT_TEACHER_CAPABILITIES = {
    function_calling: true,
    strict_structured_output: true,
    parallel_tool_calls: false,
    reasoning_effort: true,
};
const SUPERVISION_POLICIES = [
    {value: "full", label: "Full gating v1", ref: "supervision-policy:full-gating-v1", version: 1},
    {value: "selective", label: "Selective review v2", ref: "supervision-policy:selective-review-v2", version: 2},
    {value: "audit", label: "Audit-first v1", ref: "supervision-policy:audit-first-v1", version: 1},
];
const SYNTHESIS_POLICY_REFS = {
    voice: ["synthesis-policy:voice-agent-v1", 1],
    tool: ["synthesis-policy:tool-agent-v2", 2],
};
const DEPLOYMENT_POLICY_REFS = {
    production: ["deployment-policy:agent-student-v1", 1],
    conservative: ["deployment-policy:conservative-rollout-v2", 2],
};
const TRACE_POLICIES = [
    {value: "restricted", label: "Restricted learning v1", ref: "trace-policy:restricted-learning-v1", version: 1},
    {value: "minimal", label: "Minimal traces v1", ref: "trace-policy:minimal-v1", version: 1},
];
const DATA_POLICIES = [
    {value: "deidentified", label: "De-identified v1", ref: "data-policy:deidentified-agent-learning-v1", version: 1},
    {value: "synthetic", label: "Synthetic only v2", ref: "data-policy:synthetic-only-v2", version: 2},
];
const DEPLOYMENT_POLICIES = [
    {value: "production", label: "Student production v1", ref: "deployment-policy:agent-student-v1", version: 1},
    {
        value: "conservative",
        label: "Conservative rollout v2",
        ref: "deployment-policy:conservative-rollout-v2",
        version: 2
    },
];
const SUPERVISION_POLICY_REFS = {
    full: ["supervision-policy:full-gating-v1", 1],
    selective: ["supervision-policy:selective-review-v2", 2],
    audit: ["supervision-policy:audit-first-v1", 1],
};
const DATA_POLICY_REFS = {
    deidentified: ["data-policy:deidentified-agent-learning-v1", 1],
    synthetic: ["data-policy:synthetic-only-v2", 2],
};
const SYNTHESIS_POLICIES = [
    {value: "voice", label: "Voice Agent Synthesis v1", ref: "synthesis-policy:voice-agent-v1", version: 1},
    {value: "tool", label: "Tool Agent Synthesis v2", ref: "synthesis-policy:tool-agent-v2", version: 2},
];

const REVIEW_OPTIONS = [
    {value: "always", label: "Always"},
    {value: "sampled", label: "Sampled"},
    {value: "never", label: "Never"},
];

const TIMEOUT_ACTIONS = [
    {value: "safe_failure", label: "Safe failure"},
    {value: "validated_student", label: "Validated student"},
    {value: "fail_route", label: "Failure route"},
];
const TRACE_POLICY_REFS = {
    restricted: ["trace-policy:restricted-learning-v1", 1],
    minimal: ["trace-policy:minimal-v1", 1],
};

const EVAL_RELEASES = [
    {value: "workflow-v7", label: "Workflow Evaluation v7 · Passed"},
    {value: "workflow-v8", label: "Workflow Evaluation v8 · Candidate"},
];

const CAPTURE_MODES = ["none", "summary", "redacted", "full"];

const phasePresets = {
    TEACHER_PRIMARY: {responseReview: "never", readReview: "never", writeReview: "never", auditRate: 0},
    STUDENT_GATED: {responseReview: "always", readReview: "always", writeReview: "always", auditRate: 0},
    STUDENT_SUPERVISED: {responseReview: "sampled", readReview: "sampled", writeReview: "always", auditRate: .05},
    STUDENT_AUTONOMOUS: {responseReview: "never", readReview: "never", writeReview: "sampled", auditRate: .05},
};


const validateLearning = (learning) => {
    const errors = [];
    const warnings = [];

    if (!learning?.enabled) return {errors, warnings};

    const mode = learning.mode;
    const needTeacher = ["TEACHER_PRIMARY", "STUDENT_GATED", "STUDENT_SUPERVISED"].includes(mode)
        || (mode === "STUDENT_AUTONOMOUS" && (
            learning.supervision?.background_audit_sample_rate > 0 || learning.synthesis?.enabled
        ));
    const needStudent = ["STUDENT_GATED", "STUDENT_SUPERVISED", "STUDENT_AUTONOMOUS"].includes(mode);

    // Errors — blocking
    if (needTeacher && !learning.teacher?.model) {
        errors.push("A teacher model profile is required for this phase and enabled features.");
    }
    if (needStudent && !learning.student?.model) {
        errors.push("A student model profile is required for this phase.");
    }
    if (mode === "STUDENT_GATED" && [
        learning.supervision?.assistant_response_review,
        learning.supervision?.read_tool_review,
        learning.supervision?.reversible_write_review,
    ].some(v => v !== "always")) {
        errors.push("Phase 2 requires synchronous review of every decision class.");
    }
    if (learning.synthesis?.enabled && !learning.synthesis?.policy) {
        errors.push("Select a synthetic-data policy or disable synthetic generation.");
    }
    if (!learning.tracing?.enabled && ["TEACHER_PRIMARY", "STUDENT_GATED"].includes(mode)) {
        errors.push("Trace recording is required in Phase 1 and Phase 2.");
    }

    // Warnings — non-blocking
    if (needStudent && !learning.deployment?.minimum_eval_release_ref) {
        warnings.push("No minimum evaluation release is selected for the student.");
    }
    if (mode === "STUDENT_AUTONOMOUS" && (learning.supervision?.background_audit_sample_rate ?? 0) < .05) {
        warnings.push("Phase 4 background audit is below the recommended 5% sample.");
    }
    if (learning.privacy?.external_teacher_tool_result_mode === "full") {
        warnings.push("Full tool results may expose sensitive data to the external teacher.");
    }
    if (learning.privacy?.allow_real_training_data) {
        warnings.push("Real conversations are eligible for curated training.");
    }

    return {errors, warnings};
};

const sampleRateFor = (reviewType, sampledDefault) => {
    if (reviewType === "always") return 1;
    if (reviewType === "sampled") return sampledDefault;
    return 0;
};

const pct = v => Math.round(Number(v || 0) * 100) + "%";

const emptyLearning = () => ({
    config_version: "1.0",
    enabled: false,
    mode: null,
    teacher: {
        model: null,
        temperature: 0,
        reasoning_effort: null,
        max_output_tokens: null,
        timeout_ms: null,
        max_review_repairs: null
    },
    student: {
        model: null, temperature: 0, max_output_tokens: null, timeout_ms: null,
        shadow_enabled: false, shadow_sample_rate: null
    },
    supervision: {
        policy: null, assistant_response_review: null, read_tool_review: null,
        reversible_write_review: null, background_audit_sample_rate: 0,
        takeover_on_student_timeout: false, takeover_on_student_error: false,
        takeover_on_repair_exhaustion: false, takeover_on_no_progress: false, invalid_student_decision: true,
        irreversible_write_review: "always", external_communication_review: "always", unknown_tool_review: "always",
        takeover_on_validation_failure: true,
        takeover_on_policy_escalation: true,
        review_after_tool_error: true,
        max_reviews_per_turn: 10,
    },
    handover: {
        response_timeout_action: null, reversible_write_timeout_action: null,
        max_added_latency_ms: null, wait_message_after_ms: null,
        buffer_student_output: true, rejected_output_in_history: false,
        revalidate_teacher_output: true, commit_selected_decision_only: true,
        allow_preapproval_streaming: false,
        protected_tool_timeout_action: "fail_closed",
        max_takeovers_per_turn: 2,
    },
    tool_safety: {
        authorization_ttl_ms: null, unknown_effect_policy: "protected",
        require_execution_authorization: true, idempotency_enabled: true, parallel_tool_calls: false,
        require_effect_metadata: true,
        idempotency_scope: "turn_tool_arguments",
    },
    tracing: {
        policy: null, enabled: false, sample_rate: 0, store_rejected_candidates: false,
        tool_result_mode: null, emit_learning_events: false,
        store_raw_provider_response: false,
        include_successes: true,
        include_failures: true,
        snapshot_system_prompt: true,
        snapshot_tool_schemas: true,
    },
    privacy: {
        data_policy: null, external_teacher_tool_result_mode: null, allow_real_training_data: false,
        deidentify_before_external_teacher: true,
        redact_secrets: true,
    },
    synthesis: {
        enabled: false, policy: null, on_teacher_replace: false, on_validation_failure: false,
        on_user_correction: false, max_variants_per_incident: null, native_language_review: false,
        on_tool_error: false,
        language_variants: true,
        human_review_protected: true,
    },
    deployment: {policy: null, auto_regress_mode: false, auto_rollback_student: false, minimum_eval_release_ref: null},
});


export const SupervisionLearningTab = ({node, setNode, onUpdate}) => {
    const {modelDetails} = useContext(WebSettingsContext);

    const learning = node?.data?.learning || emptyLearning();
    const {errors, warnings} = validateLearning(learning);
    const nodeRef = useRef(node);
    nodeRef.current = node;
    const mode = learning.mode || "";
    const phaseLocked = mode === "STUDENT_GATED";

    const teacherHidden = mode === "STUDENT_AUTONOMOUS"
        && !(learning.supervision?.background_audit_sample_rate > 0)
        && !learning.synthesis?.enabled;
    const shadowVisible = mode === "TEACHER_PRIMARY";
    const studentHidden = mode === "TEACHER_PRIMARY" && !learning.student?.shadow_enabled;

    const onChangeLearningNow = (path, value) => {
        const base = nodeRef.current;
        const prevLearning = base?.data?.learning || emptyLearning();
        const nextLearning = {...prevLearning};
        const keys = path.split(".");
        let target = nextLearning;
        for (let i = 0; i < keys.length - 1; i++) {
            target[keys[i]] = {...(target[keys[i]] || {})};
            target = target[keys[i]];
        }
        target[keys[keys.length - 1]] = value;
        const nextNode = {...base, data: {...base.data, learning: nextLearning}};
        nodeRef.current = nextNode;
        setNode(nextNode);
        onUpdate(nextNode);
    };

    const onChangeLearning = (path, value) => {
        setNode(prev => {
            const state = {...prev, data: {...prev.data}};
            const nextLearning = {...(state.data.learning || emptyLearning())};
            const keys = path.split(".");
            let target = nextLearning;
            for (let i = 0; i < keys.length - 1; i++) {
                target[keys[i]] = {...(target[keys[i]] || {})};
                target = target[keys[i]];
            }
            target[keys[keys.length - 1]] = value;
            state.data.learning = nextLearning;
            nodeRef.current = state;
            return state;
        });
    };

    const commit = () => onUpdate(nodeRef.current);

    const onChangeEnabled = (checked) => {
        const base = nodeRef.current;
        const prevLearning = base?.data?.learning || emptyLearning();
        const nextLearning = checked
            ? {...emptyLearning(), ...prevLearning, enabled: true}
            : {config_version: "1.0", enabled: false};
        const nextNode = {...base, data: {...base.data, learning: nextLearning}};
        nodeRef.current = nextNode;
        setNode(nextNode);
        onUpdate(nextNode);
    };

    const onChangeMode = (value) => {
        const base = nodeRef.current;
        const prevLearning = base?.data?.learning || emptyLearning();
        const nextLearning = {...prevLearning, mode: value};
        const preset = phasePresets[value];
        if (preset) {
            nextLearning.supervision = {
                ...nextLearning.supervision,
                assistant_response_review: preset.responseReview,
                assistant_response_sample_rate: sampleRateFor(preset.responseReview, 0.1),
                read_tool_review: preset.readReview,
                read_tool_sample_rate: sampleRateFor(preset.readReview, 0.1),
                reversible_write_review: preset.writeReview,
                reversible_write_sample_rate: sampleRateFor(preset.writeReview, 0.25),
                background_audit_sample_rate: preset.auditRate,
            };
        }
        const nextNode = {...base, data: {...base.data, learning: nextLearning}};
        nodeRef.current = nextNode;
        setNode(nextNode);
        onUpdate(nextNode);
    };

    return (
        <>
            <Box sx={{borderBottom: 1, borderColor: "divider", pb: 0.5}}>
                <Typography fontWeight={700} sx={{pb: 1}}>Supervision &amp; Learning</Typography>
                <Typography variant={"body2"} fontSize={13}>
                    Configure teacher review, student execution, safe handover, and learning data
                </Typography>
            </Box>

            <Box sx={{px: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2}}>
                <Box sx={{minWidth: 0}}>
                    <Typography fontWeight={700} fontSize={13}>Enable supervision and learning</Typography>
                    <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                        Activates teacher–student execution and governed trace capture.
                    </Typography>
                </Box>
                <Switch
                    checked={!!learning.enabled}
                    size={"small"}
                    onChange={(event) => onChangeEnabled(event.target.checked)}
                />
            </Box>

            {learning.enabled && (
                <>
                    {(errors.length > 0 || warnings.length > 0 || mode) && (
                        <Box sx={{
                            px: 1.5, py: 1, borderRadius: 2,
                            bgcolor: errors.length ? "#fbeeee" : warnings.length ? "#fbf3e0" : "#eef6f0", mb: 2
                        }}>
                            <Typography fontWeight={700} fontSize={13} color={
                                errors.length ? "#a85a56" : warnings.length ? "#9c7f3a" : "#4a8f63"
                            }>
                                {PHASES.find(p => p.value === mode)?.label || "Not configured"}
                            </Typography>
                            <Typography fontSize={11} color={grey[600]}>
                                {errors.length > 0
                                    ? `${errors.length} error${errors.length > 1 ? "s" : ""}`
                                    : warnings.length > 0
                                        ? `${warnings.length} non-blocking warning${warnings.length > 1 ? "s" : ""}`
                                        : "Configuration is valid"}
                            </Typography>
                            {(errors.length > 0 || warnings.length > 0) && (
                                <Box component="ul" sx={{m: 0, mt: 0.5, pl: 2}}>
                                    {errors.map((msg, i) => (
                                        <Typography key={`e${i}`} component="li" fontSize={11} color="#a85a56">
                                            {msg}
                                        </Typography>
                                    ))}
                                    {warnings.map((msg, i) => (
                                        <Typography key={`w${i}`} component="li" fontSize={11} color="#9c7f3a">
                                            {msg}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                    <FormControl size={"small"} fullWidth>
                        <InputLabel shrink id="phase-label">Lifecycle phase</InputLabel>
                        <Select
                            labelId="phase-label"
                            id="phase-select"
                            value={mode}
                            label="Lifecycle phase"
                            displayEmpty
                            notched
                            onChange={(e) => onChangeMode(e.target.value)}
                        >
                            <MenuItem value={""}><em>Select phase…</em></MenuItem>
                            {PHASES.map(p => (
                                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                            ))}
                        </Select>
                        {PHASES.find(p => p.value === mode) && (
                            <Typography fontWeight={400} color={grey[500]} fontSize={11} sx={{mt: .5, px: .5}}>
                                {PHASES.find(p => p.value === mode).description}
                            </Typography>
                        )}
                    </FormControl>

                    {!teacherHidden && (
                        <>
                            <Divider sx={{my: 1}}/>
                            <FormControl size={"small"} fullWidth>
                                <InputLabel id="teacher-model-label">Teacher Model</InputLabel>
                                <Select
                                    labelId="teacher-model-label"
                                    id="teacher-model-select"
                                    value={learning.teacher?.model || ""}
                                    label="Teacher Model"
                                    onChange={(e) => onChangeLearningNow("teacher.model", e.target.value === "" ? null : e.target.value)}
                                    renderValue={(selected) => selected || ""}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {maxWidth: 320}
                                        }
                                    }}
                                >
                                    <MenuItem value={""}><em>None</em></MenuItem>
                                    {modelDetails?.map(model => (
                                        <MenuItem key={model.modelId} value={model.modelId}
                                                  sx={{whiteSpace: "normal", alignItems: "flex-start"}}>
                                            <Box sx={{minWidth: 0}}>
                                                <Typography fontSize={13}>{model.modelId}</Typography>
                                                {model.description && (
                                                    <Typography fontWeight={400} color={grey[500]} fontSize={11}
                                                                sx={{whiteSpace: "normal"}}>
                                                        {model.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction={"row"} gap={1}>
                                <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                                    <InputLabel shrink id="teacher-reasoning-label">Reasoning effort</InputLabel>
                                    <Select
                                        labelId="teacher-reasoning-label"
                                        value={learning.teacher?.reasoning_effort || ""}
                                        label="Reasoning effort"
                                        displayEmpty
                                        notched
                                        onChange={(e) => onChangeLearningNow("teacher.reasoning_effort", e.target.value)}
                                    >
                                        <MenuItem value={""}><em>Not set</em></MenuItem>
                                        {["none", "low", "medium", "high", "xhigh"].map(v => (
                                            <MenuItem key={v} value={v}>{v}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    sx={{flex: 1, minWidth: 0}}
                                    type={"number"}
                                    size={"small"}
                                    label={"Timeout (ms)"}
                                    value={learning.teacher?.timeout_ms ?? ""}
                                    onChange={(e) => onChangeLearning("teacher.timeout_ms", Number(e.target.value))}
                                    onBlur={commit}
                                />
                            </Stack>

                            <Stack direction={"row"} gap={1}>
                                <TextField
                                    sx={{flex: 1, minWidth: 0}}
                                    type={"number"}
                                    size={"small"}
                                    label={"Max output tokens"}
                                    value={learning.teacher?.max_output_tokens ?? ""}
                                    onChange={(e) => onChangeLearning("teacher.max_output_tokens", Number(e.target.value))}
                                    onBlur={commit}
                                />
                                <TextField
                                    sx={{flex: 1, minWidth: 0}}
                                    type={"number"}
                                    size={"small"}
                                    label={"Verdict repairs"}
                                    inputProps={{min: 0, max: 2}}
                                    value={learning.teacher?.max_review_repairs ?? ""}
                                    onChange={(e) => onChangeLearning("teacher.max_review_repairs", Number(e.target.value))}
                                    onBlur={commit}
                                />
                            </Stack>

                            <Box sx={{px: 1}}>
                                <Stack direction={"row"} gap={2} alignItems={"center"}
                                       justifyContent={"space-between"}>
                                    <Typography fontWeight={600} fontSize={13}>Teacher Temperature</Typography>
                                    <Typography
                                        fontSize={12}>{(learning.teacher?.temperature ?? 0).toFixed(1)}</Typography>
                                </Stack>
                                <Slider
                                    sx={{flex: 1, minWidth: 0}} size={"small"}
                                    value={learning.teacher?.temperature ?? 0}
                                    min={0} max={.5} step={.1}
                                    onChange={(e, v) => onChangeLearning("teacher.temperature", v)}
                                    onChangeCommitted={(e, v) => onChangeLearningNow("teacher.temperature", v)}
                                />
                            </Box>
                        </>
                    )}

                    {!studentHidden && (
                        <>
                            <Divider sx={{my: 1}}/>
                            <Typography fontWeight={700} fontSize={13}>Student model</Typography>
                            <FormControl size={"small"} fullWidth>
                                <InputLabel id="student-model-label">Student Model</InputLabel>
                                <Select
                                    labelId="student-model-label"
                                    id="student-model-select"
                                    value={learning.student?.model || ""}
                                    label="Student Model"
                                    onChange={(e) => onChangeLearningNow("student.model", e.target.value === "" ? null : e.target.value)}
                                    renderValue={(selected) => selected || ""}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {maxWidth: 320}
                                        }
                                    }}
                                >
                                    <MenuItem value={""}><em>None</em></MenuItem>
                                    {modelDetails?.map(model => (
                                        <MenuItem key={model.modelId} value={model.modelId}
                                                  sx={{whiteSpace: "normal", alignItems: "flex-start"}}>
                                            <Box sx={{minWidth: 0}}>
                                                <Typography fontSize={13}>{model.modelId}</Typography>
                                                {model.description && (
                                                    <Typography fontWeight={400} color={grey[500]} fontSize={11}
                                                                sx={{whiteSpace: "normal"}}>
                                                        {model.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction={"row"} gap={1}>
                                <TextField
                                    sx={{flex: 1, minWidth: 0}}
                                    type={"number"}
                                    size={"small"}
                                    label={"Timeout (ms)"}
                                    value={learning.student?.timeout_ms ?? ""}
                                    onChange={(e) => onChangeLearning("student.timeout_ms", Number(e.target.value))}
                                    onBlur={commit}
                                />
                                <TextField
                                    sx={{flex: 1, minWidth: 0}}
                                    type={"number"}
                                    size={"small"}
                                    label={"Max output tokens"}
                                    value={learning.student?.max_output_tokens ?? ""}
                                    onChange={(e) => onChangeLearning("student.max_output_tokens", Number(e.target.value))}
                                    onBlur={commit}
                                />
                            </Stack>

                            <Box sx={{px: 1}}>
                                <Stack direction={"row"} gap={2} alignItems={"center"}
                                       justifyContent={"space-between"}>
                                    <Typography fontWeight={600} fontSize={13}>Student Temperature</Typography>
                                    <Typography
                                        fontSize={12}>{(learning.student?.temperature ?? 0).toFixed(1)}</Typography>
                                </Stack>
                                <Slider
                                    sx={{flex: 1, minWidth: 0}} size={"small"}
                                    value={learning.student?.temperature ?? 0}
                                    min={0} max={1} step={.1}
                                    onChange={(e, v) => onChangeLearning("student.temperature", v)}
                                    onChangeCommitted={(e, v) => onChangeLearningNow("student.temperature", v)}
                                />
                            </Box>

                            {shadowVisible && (
                                <>
                                    <Box sx={{px: 1}}>
                                        <FormControlLabel
                                            size={"small"}
                                            control={(
                                                <Checkbox
                                                    checked={!!learning.student?.shadow_enabled}
                                                    size={"small"}
                                                    sx={{p: 0.5}}
                                                    onChange={(e) => onChangeLearningNow("student.shadow_enabled", e.target.checked)}
                                                />
                                            )}
                                            label={`Run student in shadow`}
                                        />
                                        <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                                            Trace-only; never delivered or executed
                                        </Typography>
                                    </Box>
                                    <Box sx={{px: 1}}>
                                        <Stack direction={"row"} gap={2} alignItems={"center"}
                                               justifyContent={"space-between"}>
                                            <Typography fontWeight={600} fontSize={13}>Shadow sample</Typography>
                                            <Typography
                                                fontSize={12}>{pct(learning.student?.shadow_sample_rate)}</Typography>
                                        </Stack>
                                        <Slider
                                            sx={{flex: 1, minWidth: 0}} size={"small"}
                                            value={learning.student?.shadow_sample_rate ?? 0}
                                            min={0} max={1} step={.05}
                                            onChange={(e, v) => onChangeLearning("student.shadow_sample_rate", v)}
                                            onChangeCommitted={(e, v) => onChangeLearningNow("student.shadow_sample_rate", v)}
                                        />
                                    </Box>
                                </>
                            )}
                        </>
                    )}

                    <Divider sx={{my: 1}}/>
                    <Typography fontWeight={700} fontSize={13}>Supervision policy</Typography>
                    <Stack direction={"row"} gap={1}>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel id="supervision-policy-label">Supervision policy</InputLabel>
                            <Select
                                labelId="supervision-policy-label"
                                value={learning.supervision?.policy || ""}
                                label="Supervision policy"
                                onChange={(e) => {
                                    const [ref, version] = SUPERVISION_POLICY_REFS[e.target.value] || [null, null];
                                    onChangeLearningNow("supervision", {
                                        ...learning.supervision,
                                        policy: e.target.value,
                                        policy_ref: ref,
                                        policy_version: version,
                                    });
                                }}
                            >
                                <MenuItem value={""}><em>None</em></MenuItem>
                                {SUPERVISION_POLICIES.map(p => (
                                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel id="trace-policy-label">Trace policy</InputLabel>
                            <Select
                                labelId="trace-policy-label"
                                value={learning.tracing?.policy || ""}
                                label="Trace policy"
                                onChange={(e) => {
                                    const [ref, version] = TRACE_POLICY_REFS[e.target.value] || [null, null];
                                    onChangeLearningNow("tracing", {
                                        ...learning.tracing,
                                        policy: e.target.value,
                                        policy_ref: ref,
                                        policy_version: version,
                                    });
                                }}
                            >
                                <MenuItem value={""}><em>None</em></MenuItem>
                                {TRACE_POLICIES.map(p => (
                                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={"row"} gap={1}>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel id="data-policy-label">Learning-data policy</InputLabel>
                            <Select
                                labelId="data-policy-label"
                                value={learning.privacy?.data_policy || ""}
                                label="Learning-data policy"
                                onChange={(e) => {
                                    const [ref, version] = DATA_POLICY_REFS[e.target.value] || [null, null];
                                    onChangeLearningNow("privacy", {
                                        ...learning.privacy,
                                        data_policy: e.target.value,
                                        data_policy_ref: ref,
                                        data_policy_version: version,
                                    });
                                }}>
                                <MenuItem value={""}><em>None</em></MenuItem>
                                {DATA_POLICIES.map(p => (
                                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel id="deployment-policy-label">Deployment policy</InputLabel>
                            <Select
                                labelId="deployment-policy-label"
                                value={learning.deployment?.policy || ""}
                                label="Deployment policy"
                                onChange={(e) => {
                                    const [ref, version] = DEPLOYMENT_POLICY_REFS[e.target.value] || [null, null];
                                    onChangeLearningNow("deployment", {
                                        ...learning.deployment,
                                        policy: e.target.value,
                                        policy_ref: ref,
                                        policy_version: version,
                                    });
                                }}>
                                <MenuItem value={""}><em>None</em></MenuItem>
                                {DEPLOYMENT_POLICIES.map(p => (
                                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Review rules</Typography>
                        <Stack direction="row" gap={0.5} alignItems="center">
                            {phaseLocked && <IconLock size={12} color="#f5b301"/>}
                            <Typography fontSize={10} color={phaseLocked ? "warning.main" : grey[500]}>
                                {phaseLocked ? "Phase 2 locked" : "Policy override"}
                            </Typography>
                        </Stack>
                    </Stack>

                    {[
                        {
                            key: "assistant_response_review",
                            label: "User-facing responses",
                            help: "Questions, results, completion"
                        },
                        {
                            key: "read_tool_review",
                            label: "Read-only tools",
                            help: "Lookup and retrieval"
                        },
                        {
                            key: "reversible_write_review",
                            label: "Reversible writes",
                            help: "Actions that can be undone"
                        },
                    ].map(rule => (
                        <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}
                               key={rule.key}>
                            <Box sx={{minWidth: 0}}>
                                <Typography fontSize={13}>{rule.label}</Typography>
                                <Typography fontWeight={400} color={grey[500]}
                                            fontSize={11}>{rule.help}</Typography>
                            </Box>
                            <FormControl size={"small"} sx={{minWidth: 120}} disabled={phaseLocked}>
                                <Select
                                    value={learning.supervision?.[rule.key] || ""}
                                    displayEmpty
                                    onChange={(e) => {
                                        const sampledDefault = rule.key === "reversible_write_review" ? 0.25 : 0.1;
                                        const rateKey = rule.key.replace("_review", "_sample_rate");
                                        onChangeLearningNow("supervision", {
                                            ...learning.supervision,
                                            [rule.key]: e.target.value,
                                            [rateKey]: sampleRateFor(e.target.value, sampledDefault),
                                        });
                                    }}
                                >
                                    <MenuItem value={""}><em>Not set</em></MenuItem>
                                    {REVIEW_OPTIONS.map(o => (
                                        <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    ))}
                    {[
                        {label: "Irreversible writes", help: "Always synchronously reviewed"},
                        {label: "External communication", help: "Calls, messages, and external actions"},
                        {label: "Unknown-effect tools", help: "Missing metadata is protected"},
                    ].map(row => (
                        <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}
                               key={row.label}>
                            <Box sx={{minWidth: 0}}>
                                <Typography fontSize={13}>{row.label}</Typography>
                                <Typography fontWeight={400} color={grey[500]} fontSize={11}>{row.help}</Typography>
                            </Box>
                            <Stack direction="row" gap={0.5} alignItems="center" sx={{flexShrink: 0}}>
                                <IconLock size={12} color="#f5b301"/>
                                <Typography fontSize={11} fontWeight={700} color={grey[700]}>Always</Typography>
                            </Stack>
                        </Stack>
                    ))}

                    <Box sx={{px: 1}}>
                        <Stack direction="row" gap={0.5} alignItems="flex-start">
                            <IconLock size={13} color="#f5b301" style={{marginTop: 2, flexShrink: 0}}/>
                            <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                                Irreversible writes, external communication, and unknown-effect tools are always
                                synchronously reviewed.
                            </Typography>
                        </Stack>
                    </Box>

                    <Box sx={{px: 1}}>
                        <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography fontWeight={600} fontSize={13}>Background teacher audit</Typography>
                            <Typography
                                fontSize={12}>{pct(learning.supervision?.background_audit_sample_rate)}</Typography>
                        </Stack>
                        <Slider
                            sx={{flex: 1, minWidth: 0}} size={"small"}
                            value={learning.supervision?.background_audit_sample_rate ?? 0}
                            min={0} max={1} step={.01}
                            onChange={(e, v) => onChangeLearning("supervision.background_audit_sample_rate", v)}
                            onChangeCommitted={(e, v) => onChangeLearningNow("supervision.background_audit_sample_rate", v)}
                        />
                    </Box>

                    <Typography fontWeight={700} fontSize={12}>Takeover triggers</Typography>

                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={true}
                                    disabled
                                    size={"small"}
                                    sx={{p: 0.5}}
                                />
                            )}
                            label={(
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    <span>Invalid student decision</span>
                                    <IconLock size={12} color="#f5b301"/>
                                </Stack>
                            )}
                        />
                        <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                            Schema, tool, arguments, or output
                        </Typography>
                    </Box>

                    {[
                        {
                            key: "takeover_on_student_timeout",
                            label: "Timeout or provider error",
                            help: "Teacher produces the decision"
                        },
                        {
                            key: "takeover_on_repair_exhaustion",
                            label: "No progress or repair exhausted",
                            help: "Bounded teacher takeover"
                        },
                    ].map(t => (
                        <Box sx={{px: 1}} key={t.key}>
                            <FormControlLabel
                                size={"small"}
                                control={(
                                    <Checkbox
                                        checked={!!learning.supervision?.[t.key]}
                                        size={"small"}
                                        sx={{p: 0.5}}
                                        onChange={(e) => {
                                            if (t.key === "takeover_on_student_timeout") {
                                                onChangeLearningNow("supervision", {
                                                    ...learning.supervision,
                                                    takeover_on_student_timeout: e.target.checked,
                                                    takeover_on_student_error: e.target.checked,
                                                });
                                            } else {
                                                onChangeLearningNow("supervision", {
                                                    ...learning.supervision,
                                                    takeover_on_repair_exhaustion: e.target.checked,
                                                    takeover_on_no_progress: e.target.checked,
                                                });
                                            }
                                        }}/>
                                )}
                                label={t.label}
                            />
                            <Typography fontWeight={400} color={grey[500]} fontSize={11}>{t.help}</Typography>
                        </Box>
                    ))}

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Safe handover and timeouts</Typography>
                        <Typography fontSize={10} color={grey[500]}>4 invariants</Typography>
                    </Stack>

                    {[
                        {label: "Buffer student output", help: "No text or TTS before approval", on: true},
                        {label: "Rejected output in history", help: "Candidate stays in trace only", on: false},
                        {
                            label: "Revalidate teacher replacement",
                            help: "Same schema and tool validation",
                            on: true
                        },
                        {label: "Commit selected decision only", help: "One authoritative history entry", on: true},
                    ].map(row => (
                        <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}
                               key={row.label}>
                            <Box sx={{minWidth: 0}}>
                                <Typography fontSize={13}>{row.label}</Typography>
                                <Typography fontWeight={400} color={grey[500]} fontSize={11}>{row.help}</Typography>
                            </Box>
                            <Stack direction="row" gap={0.5} alignItems="center" sx={{flexShrink: 0}}>
                                <IconLock size={12} color="#f5b301"/>
                                <Typography fontSize={11} fontWeight={700}
                                            color={grey[700]}>{row.on ? "On" : "Off"}</Typography>
                            </Stack>
                        </Stack>
                    ))}

                    <Typography fontWeight={700} fontSize={12} sx={{mt: 1}}>Teacher timeout actions</Typography>
                    <Stack direction={"row"} gap={1}>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel id="response-timeout-label">Response/read</InputLabel>
                            <Select
                                labelId="response-timeout-label"
                                value={learning.handover?.response_timeout_action || ""}
                                label="Response/read"
                                onChange={(e) => {
                                    onChangeLearningNow("handover", {
                                        ...learning.handover,
                                        response_timeout_action: e.target.value,
                                        read_tool_timeout_action: e.target.value,
                                    });
                                }}>
                                <MenuItem value={""}><em>None</em></MenuItem>
                                {TIMEOUT_ACTIONS.map(o => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel id="write-timeout-label">Reversible write</InputLabel>
                            <Select
                                labelId="write-timeout-label"
                                value={learning.handover?.reversible_write_timeout_action || ""}
                                label="Reversible write"
                                onChange={(e) => onChangeLearningNow("handover.reversible_write_timeout_action", e.target.value)}
                            >
                                <MenuItem value={""}><em>None</em></MenuItem>
                                {TIMEOUT_ACTIONS.map(o => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction={"row"} gap={1}>
                        <TextField
                            sx={{flex: 1, minWidth: 0}}
                            type={"number"}
                            size={"small"}
                            label={"Added latency (ms)"}
                            value={learning.handover?.max_added_latency_ms ?? ""}
                            onChange={(e) => onChangeLearning("handover.max_added_latency_ms", Number(e.target.value))}
                            onBlur={commit}
                        />
                        <TextField
                            sx={{flex: 1, minWidth: 0}}
                            type={"number"}
                            size={"small"}
                            label={"Wait message after (ms)"}
                            value={learning.handover?.wait_message_after_ms ?? ""}
                            onChange={(e) => onChangeLearning("handover.wait_message_after_ms", Number(e.target.value))}
                            onBlur={commit}
                        />
                    </Stack>
                    <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                        Protected and unknown-effect tools always fail closed when required teacher approval is
                        unavailable.
                    </Typography>

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Tool execution safety</Typography>
                        <Typography fontSize={10} color={grey[500]}>2 tools need metadata</Typography>
                    </Stack>

                    {[
                        {
                            label: "Unknown effects are protected",
                            help: "Safe default for incomplete MCP metadata",
                            on: true
                        },
                        {
                            label: "Execution authorization",
                            help: "Selected decision receives a short-lived token",
                            on: true
                        },
                        {label: "Duplicate execution prevention", help: "Turn + tool + arguments key", on: true},
                        {label: "Parallel tool calls", help: "One semantic decision per round", on: false},
                    ].map(row => (
                        <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}
                               key={row.label}>
                            <Box sx={{minWidth: 0}}>
                                <Typography fontSize={13}>{row.label}</Typography>
                                <Typography fontWeight={400} color={grey[500]} fontSize={11}>{row.help}</Typography>
                            </Box>
                            <Stack direction="row" gap={0.5} alignItems="center" sx={{flexShrink: 0}}>
                                <IconLock size={12} color="#f5b301"/>
                                <Typography fontSize={11} fontWeight={700}
                                            color={grey[700]}>{row.on ? "On" : "Off"}</Typography>
                            </Stack>
                        </Stack>
                    ))}

                    <TextField
                        type={"number"}
                        size={"small"}
                        label={"Authorization lifetime (ms)"}
                        value={learning.tool_safety?.authorization_ttl_ms ?? ""}
                        onChange={(e) => onChangeLearning("tool_safety.authorization_ttl_ms", Number(e.target.value))}
                        onBlur={commit}
                    />

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Trace and learning data</Typography>
                        <Typography fontSize={10} color={grey[500]}>Recording</Typography>
                    </Stack>
                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={!!learning.tracing?.enabled}
                                    size={"small"}
                                    sx={{p: 0.5}}
                                    onChange={(e) => onChangeLearningNow("tracing.enabled", e.target.checked)}
                                />
                            )}
                            label={`Record decision traces`}
                        />
                    </Box>
                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={!!learning.tracing?.store_rejected_candidates}
                                    size={"small"}
                                    sx={{p: 0.5}}
                                    onChange={(e) => onChangeLearningNow("tracing.store_rejected_candidates", e.target.checked)}
                                />
                            )}
                            label={`Store rejected candidates`}
                        />
                    </Box>
                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={!!learning.tracing?.emit_learning_events}
                                    size={"small"}
                                    sx={{p: 0.5}}
                                    onChange={(e) => onChangeLearningNow("tracing.emit_learning_events", e.target.checked)}
                                />
                            )}
                            label={`Emit learning events`}
                        />
                    </Box>

                    <Box sx={{px: 1}}>
                        <Stack direction={"row"} gap={2} alignItems={"center"} justifyContent={"space-between"}>
                            <Typography fontWeight={600} fontSize={13}>Trace sample</Typography>
                            <Typography fontSize={12}>{pct(learning.tracing?.sample_rate)}</Typography>
                        </Stack>
                        <Slider
                            sx={{flex: 1, minWidth: 0}} size={"small"}
                            value={learning.tracing?.sample_rate ?? 0}
                            min={0} max={1} step={.05}
                            onChange={(e, v) => onChangeLearning("tracing.sample_rate", v)}
                            onChangeCommitted={(e, v) => onChangeLearningNow("tracing.sample_rate", v)}
                        />
                    </Box>

                    <Stack direction={"row"} gap={1}>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel shrink id="tool-mode-label">Tool-result capture</InputLabel>
                            <Select
                                labelId="tool-mode-label"
                                value={learning.tracing?.tool_result_mode || ""}
                                label="Tool-result capture"
                                displayEmpty
                                notched
                                onChange={(e) => onChangeLearningNow("tracing.tool_result_mode", e.target.value)}
                            >
                                <MenuItem value={""}><em>Not set</em></MenuItem>
                                {CAPTURE_MODES.map(v => (
                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                            <InputLabel shrink id="external-mode-label">External teacher receives</InputLabel>
                            <Select
                                labelId="external-mode-label"
                                value={learning.privacy?.external_teacher_tool_result_mode || ""}
                                label="External teacher receives"
                                displayEmpty
                                notched
                                onChange={(e) => onChangeLearningNow("privacy.external_teacher_tool_result_mode", e.target.value)}
                            >
                                <MenuItem value={""}><em>Not set</em></MenuItem>
                                {CAPTURE_MODES.map(v => (
                                    <MenuItem key={v} value={v}>{v}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={!!learning.privacy?.allow_real_training_data}
                                    size={"small"}
                                    sx={{p: 0.5}}
                                    onChange={(e) => {
                                        onChangeLearningNow("privacy", {
                                            ...learning.privacy,
                                            allow_real_training_data: e.target.checked,
                                            synthetic_only: !e.target.checked,
                                        });
                                    }}/>
                            )}
                            label={`Allow real conversations for training`}
                        />
                        <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                            Requires approved data policy
                        </Typography>
                    </Box>

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Synthetic data</Typography>
                        <Typography fontSize={10} color={grey[500]}>
                            {learning.synthesis?.enabled ? "Enabled" : "Disabled"}
                        </Typography>
                    </Stack>
                    <Box sx={{
                        px: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2
                    }}>
                        <Box sx={{minWidth: 0}}>
                            <Typography fontWeight={700} fontSize={13}>Generate synthetic examples</Typography>
                            <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                                Runs asynchronously after incidents
                            </Typography>
                        </Box>
                        <Switch
                            checked={!!learning.synthesis?.enabled}
                            size={"small"}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                onChangeLearningNow("synthesis", {
                                    ...learning.synthesis,
                                    enabled: checked,
                                    ...(checked ? {} : {policy: null, policy_ref: null, policy_version: null}),
                                });
                            }}
                        />
                    </Box>

                    {learning.synthesis?.enabled && (
                        <>
                            <FormControl size={"small"} fullWidth>
                                <InputLabel id="synth-policy-label">Synthetic-data policy</InputLabel>
                                <Select
                                    labelId="synth-policy-label"
                                    value={learning.synthesis?.policy || ""}
                                    label="Synthetic-data policy"
                                    onChange={(e) => {
                                        const [ref, version] = SYNTHESIS_POLICY_REFS[e.target.value] || [null, null];
                                        onChangeLearningNow("synthesis", {
                                            ...learning.synthesis,
                                            policy: e.target.value,
                                            policy_ref: ref,
                                            policy_version: version,
                                        });
                                    }}>
                                    <MenuItem value={""}><em>Select policy…</em></MenuItem>
                                    {SYNTHESIS_POLICIES.map(p => (
                                        <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Stack direction={"row"} gap={1}>
                                <TextField
                                    sx={{flex: 1, minWidth: 0}}
                                    type={"number"}
                                    size={"small"}
                                    label={"Maximum variants"}
                                    inputProps={{min: 1, max: 50}}
                                    value={learning.synthesis?.max_variants_per_incident ?? ""}
                                    onChange={(e) => onChangeLearning("synthesis.max_variants_per_incident", Number(e.target.value))}
                                    onBlur={commit}
                                />
                                <FormControl size={"small"} sx={{flex: 1, minWidth: 0}}>
                                    <InputLabel id="native-review-label">Native-language review</InputLabel>
                                    <Select
                                        labelId="native-review-label"
                                        value={learning.synthesis?.native_language_review ? "true" : "false"}
                                        label="Native-language review"
                                        onChange={(e) => onChangeLearningNow("synthesis.native_language_review", e.target.value === "true")}
                                    >
                                        <MenuItem value={"true"}>Required</MenuItem>
                                        <MenuItem value={"false"}>Not required</MenuItem>
                                    </Select>
                                </FormControl>
                            </Stack>

                            {[
                                {key: "on_teacher_replace", label: "Teacher replacement incidents"},
                                {key: "on_validation_failure", label: "Validation failure incidents"},
                                {key: "on_user_correction", label: "User correction incidents"},
                            ].map(s => (
                                <Box sx={{px: 1}} key={s.key}>
                                    <FormControlLabel
                                        size={"small"}
                                        control={(
                                            <Checkbox
                                                checked={!!learning.synthesis?.[s.key]}
                                                size={"small"}
                                                sx={{p: 0.5}}
                                                onChange={(e) => onChangeLearningNow(`synthesis.${s.key}`, e.target.checked)}
                                            />
                                        )}
                                        label={s.label}
                                    />
                                </Box>
                            ))}
                        </>
                    )}

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Deployment and drift</Typography>
                        <Typography fontSize={10} color={grey[500]}>Auto-protection</Typography>
                    </Stack>
                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={!!learning.deployment?.auto_regress_mode}
                                    size={"small"}
                                    sx={{p: 0.5}}
                                    onChange={(e) => onChangeLearningNow("deployment.auto_regress_mode", e.target.checked)}
                                />
                            )}
                            label={`Return to stronger supervision`}
                        />
                        <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                            Effective mode may regress automatically
                        </Typography>
                    </Box>
                    <Box sx={{px: 1}}>
                        <FormControlLabel
                            size={"small"}
                            control={(
                                <Checkbox
                                    checked={!!learning.deployment?.auto_rollback_student}
                                    size={"small"}
                                    sx={{p: 0.5}}
                                    onChange={(e) => onChangeLearningNow("deployment.auto_rollback_student", e.target.checked)}
                                />
                            )}
                            label={`Roll back inefficient student`}
                        />
                        <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                            Restore last approved model release
                        </Typography>
                    </Box>
                    <FormControl size={"small"} fullWidth>
                        <InputLabel shrink id="eval-release-label">Minimum evaluation release</InputLabel>
                        <Select
                            labelId="eval-release-label"
                            value={(learning.deployment?.minimum_eval_release_ref || "").replace("eval-release:", "")}
                            label="Minimum evaluation release"
                            displayEmpty
                            notched
                            onChange={(e) => onChangeLearningNow(
                                "deployment.minimum_eval_release_ref",
                                e.target.value ? "eval-release:" + e.target.value : null
                            )}
                        >
                            <MenuItem value={""}><em>No release selected</em></MenuItem>
                            {EVAL_RELEASES.map(r => (
                                <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Typography fontWeight={700} fontSize={13}>Runtime status</Typography>
                        <Typography fontSize={10} color="#4a8f63" fontWeight={700}>Healthy</Typography>
                    </Stack>

                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Box sx={{minWidth: 0}}>
                            <Typography fontSize={13}>Effective mode</Typography>
                            <Typography fontWeight={400} color={grey[500]} fontSize={11}>
                                {PHASES.find(p => p.value === mode)?.label.split("— ")[1] || "Not configured"}
                            </Typography>
                        </Box>
                        <Typography fontSize={11} fontWeight={700} color="#7c6fd6"
                                    sx={{flexShrink: 0}}>{mode ? `P${PHASES.findIndex(p => p.value === mode) + 1}` : "—"}</Typography>
                    </Stack>

                    {[
                        {label: "Teacher review rate", help: "Last 24 hours", value: "100%"},
                        {label: "Teacher replacement rate", help: "Last 24 hours", value: "8.4%"},
                        {label: "Added latency p95", help: "Review and selection", value: "1.8 s"},
                    ].map(row => (
                        <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}
                               key={row.label}>
                            <Box sx={{minWidth: 0}}>
                                <Typography fontSize={13}>{row.label}</Typography>
                                <Typography fontWeight={400} color={grey[500]} fontSize={11}>{row.help}</Typography>
                            </Box>
                            <Typography fontSize={14} fontWeight={700} sx={{flexShrink: 0}}>{row.value}</Typography>
                        </Stack>
                    ))}

                    <Stack direction={"row"} gap={1} alignItems={"center"} justifyContent={"space-between"}>
                        <Box sx={{minWidth: 0}}>
                            <Typography fontSize={13}>Learning-event queue</Typography>
                            <Typography fontWeight={400} color={grey[500]} fontSize={11}>No pending
                                incidents</Typography>
                        </Box>
                        <Typography fontSize={11} fontWeight={700} color="#4a8f63"
                                    sx={{flexShrink: 0}}>Healthy</Typography>
                    </Stack>

                    <Divider sx={{my: 1}}/>
                    <Stack direction={"row"} gap={1} alignItems={"center"} sx={{marginLeft: 'auto'}}>
                        <Button
                            onClick={() => toast.warning(`Valid with warnings.`)}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: 'transparent',
                                color: 'text.primary',
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                px: 1.75,
                                height: 32,
                                '&:hover': {backgroundColor: 'grey.100', boxShadow: 'none'},
                            }}
                        >
                            Validate
                        </Button>
                        <Button
                            onClick={() => toast.success(`Dry-run passed: No tool was executed.`)}
                            sx={{
                                borderRadius: 2,
                                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                                color: 'primary.main',
                                fontSize: '0.8125rem',
                                fontWeight: 500,
                                textTransform: 'none',
                                boxShadow: 'none',
                                px: 1.75,
                                height: 32,
                                '&:hover': {
                                    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.18),
                                    boxShadow: 'none'
                                },
                            }}
                        >
                            Dry-run
                        </Button>
                    </Stack>
                </>
            )}
        </>
    );
};